import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { BusinessCard } from '../components/BusinessCard';
import { BusinessListView } from '../components/BusinessListView';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { enrichBusinessData, optimizeRoute } from '../services/geminiProxyService';
import { exportToCsv } from '../utils/csvExporter';
import {
  fetchSavedSearches,
  loadSavedSearch,
  combineSavedSearches,
  deleteSavedSearch,
  saveSearch,
  updateSavedSearch
} from '../services/savedSearchesService';
import {
  getGoogleAuthStatus,
  exportToGoogleSheets,
  appendToGoogleSheet,
  getGoogleAuthUrl
} from '../services/googleSheetsService';
import { GoogleSheetsExportModal, type ExportOptions } from '../components/GoogleSheetsExportModal';
import type { Business, SavedSearch } from '../types';

export default function SavedSearches() {
  const { user } = useAuth();

  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [selectedSavedSearchIds, setSelectedSavedSearchIds] = useState<Set<number>>(new Set());
  const [currentResults, setCurrentResults] = useState<Business[]>([]);
  const [selectedBusinesses, setSelectedBusinesses] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [columnCount, setColumnCount] = useState<3 | 4 | 5>(3);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [filterBy, setFilterBy] = useState<'all' | 'state' | 'city' | 'query'>('all');
  const [filterValue, setFilterValue] = useState('');
  const [hasGoogleAuth, setHasGoogleAuth] = useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportBusinesses, setExportBusinesses] = useState<Business[]>([]);

  // Get grid column classes based on selected column count
  const getGridClasses = () => {
    const baseClasses = 'grid grid-cols-1 md:grid-cols-2 gap-4';
    const columnClasses = {
      3: 'lg:grid-cols-3',
      4: 'lg:grid-cols-4',
      5: 'lg:grid-cols-5',
    };
    return `${baseClasses} ${columnClasses[columnCount]}`;
  };

  // Load saved searches on mount
  useEffect(() => {
    loadSavedSearchesList();
    checkGoogleAuthStatus();
  }, []);

  const checkGoogleAuthStatus = async () => {
    try {
      const status = await getGoogleAuthStatus();
      setHasGoogleAuth(status.hasGoogleAuth && status.isTokenValid);
    } catch (err) {
      console.error('Failed to check Google auth status:', err);
      setHasGoogleAuth(false);
    } finally {
      setIsGoogleAuthLoading(false);
    }
  };

  const loadSavedSearchesList = async () => {
    setIsLoading(true);
    try {
      const searches = await fetchSavedSearches();
      setSavedSearches(searches);
    } catch (err) {
      console.error('Failed to load saved searches:', err);
      setError('Failed to load saved searches');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSavedSearchSelection = (id: number) => {
    setSelectedSavedSearchIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleBusinessSelection = (id: number) => {
    setSelectedBusinesses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleViewSearch = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const savedSearch = await loadSavedSearch(id);
      setCurrentResults(savedSearch.results);
      setSelectedBusinesses(new Set());
      setSuccessMessage(`Loaded "${savedSearch.name}" with ${savedSearch.results.length} results`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to load saved search');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSearch = async (id: number) => {
    const confirmed = confirm('Are you sure you want to delete this saved search?');
    if (!confirmed) return;

    try {
      await deleteSavedSearch(id);
      setSuccessMessage('Search deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadSavedSearchesList();

      // Clear current results if we deleted the loaded search
      if (currentResults.length > 0) {
        setCurrentResults([]);
        setSelectedBusinesses(new Set());
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete search');
    }
  };

  const handleCombineSelected = async () => {
    if (selectedSavedSearchIds.size < 2) {
      setError('Please select at least 2 searches to combine');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const combined = await combineSavedSearches(Array.from(selectedSavedSearchIds));
      setCurrentResults(combined.results);
      setSelectedBusinesses(new Set());
      setSelectedSavedSearchIds(new Set());
      setSuccessMessage(`Combined ${selectedSavedSearchIds.size} searches into ${combined.resultCount} results!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to combine searches');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportSelected = async () => {
    if (selectedSavedSearchIds.size === 0) {
      setError('Please select at least one search to export');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const combined = await combineSavedSearches(Array.from(selectedSavedSearchIds));
      exportToCsv(combined.results, `saved-searches-export-${Date.now()}.csv`);
      setSuccessMessage(`Exported ${combined.resultCount} results from ${selectedSavedSearchIds.size} searches!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to export searches');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportToSheets = async () => {
    if (!hasGoogleAuth) {
      setError('Please connect your Google account first from the Account page');
      return;
    }

    const selected = currentResults.filter(b => selectedBusinesses.has(b.id));
    if (selected.length === 0) {
      setError('Please select at least one business to export');
      return;
    }

    setExportBusinesses(selected);
    setShowExportModal(true);
  };

  const handleModalExport = async (options: ExportOptions) => {
    setIsLoading(true);
    setShowExportModal(false);
    setError(null);

    try {
      // Filter data to selected columns only
      const filteredData = exportBusinesses.map(business =>
        Object.fromEntries(
          options.selectedColumns.map(col => [col, business[col as keyof Business]])
        )
      );

      if (options.mode === 'create') {
        const result = await exportToGoogleSheets(filteredData, options.title, options.selectedColumns);
        setSuccessMessage(`Exported ${filteredData.length} businesses to Google Sheets!`);
        setTimeout(() => {
          setSuccessMessage(null);
          window.open(result.spreadsheetUrl, '_blank');
        }, 2000);
      } else {
        await appendToGoogleSheet(options.spreadsheetId!, filteredData, options.sheetName, options.selectedColumns);
        setSuccessMessage(`Appended ${filteredData.length} rows to Google Sheets!`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      if (err.message === 'GOOGLE_AUTH_REQUIRED') {
        setError('Please connect your Google account first from the Account page');
      } else {
        setError(err.message || 'Failed to export to Google Sheets');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectAll = () => {
    setSelectedBusinesses(new Set(currentResults.map(b => b.id)));
  };

  const deselectAll = () => {
    setSelectedBusinesses(new Set());
  };

  const handleExport = () => {
    const selected = currentResults.filter(b => selectedBusinesses.has(b.id));
    if (selected.length === 0) {
      setError('Please select at least one business to export');
      return;
    }

    try {
      exportToCsv(selected, `business-export-${Date.now()}.csv`);
      setError(null);
      setSuccessMessage(`Exported ${selected.length} businesses!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to export businesses');
    }
  };

  const handleEnrich = async () => {
    const selected = currentResults.filter(b => selectedBusinesses.has(b.id));
    if (selected.length === 0) {
      setError('Please select at least one business to enrich');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const enrichmentOptions = ['website', 'email', 'phone', 'instagram', 'facebook', 'linkedin'];
      const enrichedResults = await Promise.all(
        currentResults.map(async (business) => {
          if (selectedBusinesses.has(business.id)) {
            try {
              const enrichedData = await enrichBusinessData(business, enrichmentOptions);
              return { ...business, ...enrichedData };
            } catch (err) {
              console.error(`Failed to enrich ${business.name}`);
              return business;
            }
          }
          return business;
        })
      );

      setCurrentResults(enrichedResults);
      setSuccessMessage(`Enriched ${selected.length} businesses!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to enrich business data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptimizeRoute = async () => {
    const selected = currentResults.filter(b => selectedBusinesses.has(b.id));
    if (selected.length < 2) {
      setError('Please select at least 2 businesses to optimize route');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const addresses = selected.map(b => b.address).filter(Boolean) as string[];
      const optimizedAddresses = await optimizeRoute(addresses, null, null, null);

      // Reorder results based on optimized route
      const addressToIndex = new Map(optimizedAddresses.map((addr, idx) => [addr, idx]));
      const reordered = [...currentResults].sort((a, b) => {
        const aIndex = addressToIndex.get(a.address) ?? 999;
        const bIndex = addressToIndex.get(b.address) ?? 999;
        return aIndex - bIndex;
      });

      setCurrentResults(reordered);
      setSuccessMessage('Route optimized successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to optimize route');
    } finally {
      setIsLoading(false);
    }
  };

  // Extract unique values for filters
  const getUniqueFilterValues = () => {
    if (filterBy === 'all') return [];

    const values = new Set<string>();
    savedSearches.forEach(search => {
      const query = search.query.toLowerCase();

      if (filterBy === 'state') {
        // Extract state from query (looking for state names/abbreviations)
        const stateMatch = query.match(/\b([A-Z]{2})\b/i) || query.match(/\b(alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming)\b/i);
        if (stateMatch) values.add(stateMatch[1].toUpperCase());
      } else if (filterBy === 'city') {
        // Try to extract city names (words before "in" or state abbreviations)
        const cityMatch = query.match(/in\s+([a-z\s]+?)(?:\s+[A-Z]{2}|$)/i);
        if (cityMatch) values.add(cityMatch[1].trim());
      } else if (filterBy === 'query') {
        // Extract main query terms (first few words)
        const mainQuery = query.split(/\s+in\s+/i)[0].trim();
        if (mainQuery) values.add(mainQuery);
      }
    });

    return Array.from(values).sort();
  };

  // Filter searches based on selection
  const filteredSearches = savedSearches.filter(search => {
    if (filterBy === 'all' || !filterValue) return true;

    const query = search.query.toLowerCase();
    const lowerFilterValue = filterValue.toLowerCase();

    if (filterBy === 'state') {
      return query.includes(lowerFilterValue);
    } else if (filterBy === 'city') {
      return query.includes(lowerFilterValue);
    } else if (filterBy === 'query') {
      return query.startsWith(lowerFilterValue) || query.includes(lowerFilterValue);
    }

    return true;
  });

  // Pagination for saved searches table
  const totalPages = Math.ceil(filteredSearches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSearches = filteredSearches.slice(startIndex, endIndex);

  // Helper to select all searches on current page
  const selectCurrentPage = () => {
    const newSelection = new Set(selectedSavedSearchIds);
    paginatedSearches.forEach(s => newSelection.add(s.id));
    setSelectedSavedSearchIds(newSelection);
  };

  // Helper to select all searches across all pages
  const selectAllPages = () => {
    setSelectedSavedSearchIds(new Set(filteredSearches.map(s => s.id)));
  };

  // Helper to deselect all
  const deselectAllSearches = () => {
    setSelectedSavedSearchIds(new Set());
  };

  // Helper to select range of pages
  const selectPageRange = (start: number, end: number) => {
    const newSelection = new Set(selectedSavedSearchIds);
    const rangeStart = (start - 1) * itemsPerPage;
    const rangeEnd = end * itemsPerPage;
    filteredSearches.slice(rangeStart, rangeEnd).forEach(s => newSelection.add(s.id));
    setSelectedSavedSearchIds(newSelection);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-white mb-2">Saved Searches</h1>
          <p className="text-blue-50">
            Manage, combine, and export your saved business searches
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-900/50 border border-green-600 rounded-lg p-4 text-green-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-600 rounded-lg p-4 text-red-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Saved Searches Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 border-b border-slate-700 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-100">Your Saved Searches</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleCombineSelected}
                  disabled={selectedSavedSearchIds.size < 2}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-all text-sm"
                >
                  Combine Selected ({selectedSavedSearchIds.size})
                </button>
                <button
                  onClick={handleExportSelected}
                  disabled={selectedSavedSearchIds.size === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors text-sm"
                >
                  Export Selected
                </button>
              </div>
            </div>

            {/* Filter and Selection Controls */}
            <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-slate-700">
              {/* Filter Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-400">Filter by:</label>
                <select
                  value={filterBy}
                  onChange={(e) => {
                    setFilterBy(e.target.value as 'all' | 'state' | 'city' | 'query');
                    setFilterValue('');
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="all">All Searches</option>
                  <option value="state">State</option>
                  <option value="city">City</option>
                  <option value="query">Query</option>
                </select>

                {filterBy !== 'all' && (
                  <select
                    value={filterValue}
                    onChange={(e) => {
                      setFilterValue(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-w-[150px]"
                  >
                    <option value="">Select {filterBy}...</option>
                    {getUniqueFilterValues().map(val => (
                      <option key={val} value={val}>{val}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Selection Controls */}
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={selectCurrentPage}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs font-medium transition-colors"
                >
                  Select Page
                </button>
                <button
                  onClick={selectAllPages}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs font-medium transition-colors"
                >
                  Select All ({filteredSearches.length})
                </button>
                <button
                  onClick={deselectAllSearches}
                  disabled={selectedSavedSearchIds.size === 0}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-slate-200 rounded-lg text-xs font-medium transition-colors"
                >
                  Clear Selection
                </button>

                {/* Page Range Selector */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2 pl-2 border-l border-slate-600">
                    <span className="text-xs text-slate-400">Pages:</span>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      placeholder="From"
                      className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-200 text-xs focus:ring-2 focus:ring-cyan-500"
                      id="page-range-start"
                    />
                    <span className="text-xs text-slate-400">to</span>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      placeholder="To"
                      className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-200 text-xs focus:ring-2 focus:ring-cyan-500"
                      id="page-range-end"
                    />
                    <button
                      onClick={() => {
                        const startInput = document.getElementById('page-range-start') as HTMLInputElement;
                        const endInput = document.getElementById('page-range-end') as HTMLInputElement;
                        const start = parseInt(startInput.value);
                        const end = parseInt(endInput.value);
                        if (start && end && start <= end && start >= 1 && end <= totalPages) {
                          selectPageRange(start, end);
                        }
                      }}
                      className="px-3 py-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded text-xs font-medium transition-all"
                    >
                      Select Range
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isLoading && savedSearches.length === 0 ? (
            <div className="p-8">
              <LoadingSpinner message="Loading saved searches..." />
            </div>
          ) : savedSearches.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <h3 className="text-xl font-semibold text-slate-300 mb-2">No Saved Searches</h3>
              <p className="text-slate-500">
                Go to the Search page to create and save your first search
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={paginatedSearches.every(s => selectedSavedSearchIds.has(s.id)) && paginatedSearches.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Add current page to selection
                              setSelectedSavedSearchIds(prev => {
                                const newSet = new Set(prev);
                                paginatedSearches.forEach(s => newSet.add(s.id));
                                return newSet;
                              });
                            } else {
                              // Remove current page from selection
                              setSelectedSavedSearchIds(prev => {
                                const newSet = new Set(prev);
                                paginatedSearches.forEach(s => newSet.delete(s.id));
                                return newSet;
                              });
                            }
                          }}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Query</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Results</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {paginatedSearches.map((search) => (
                      <tr key={search.id} className="hover:bg-slate-900/50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSavedSearchIds.has(search.id)}
                            onChange={() => toggleSavedSearchSelection(search.id)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-100">{search.name}</td>
                        <td className="px-4 py-3 text-sm text-slate-400 max-w-xs truncate">{search.query}</td>
                        <td className="px-4 py-3 text-sm text-slate-300">{search.resultCount}</td>
                        <td className="px-4 py-3 text-sm text-slate-400">{formatDate(search.createdAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewSearch(search.id)}
                              className="px-3 py-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded text-xs font-medium transition-all"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteSearch(search.id)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t border-slate-700 flex items-center justify-between">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Results Section */}
        {currentResults.length > 0 && (
          <>
            {/* Action Buttons */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-medium">
                    {selectedBusinesses.size} of {currentResults.length} selected
                  </span>
                  <button
                    onClick={selectAll}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-all"
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAll}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Deselect All
                  </button>

                  {/* View Toggle */}
                  <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`px-3 py-1 rounded-md transition-colors ${
                        viewMode === 'card'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                          : 'text-slate-300 hover:text-white'
                      }`}
                      title="Card View"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1 rounded-md transition-colors ${
                        viewMode === 'list'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                          : 'text-slate-300 hover:text-white'
                      }`}
                      title="List View"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  {/* Column Count Selector */}
                  {viewMode === 'card' && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-400">Columns:</span>
                      <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                        {[3, 4, 5].map((count) => (
                          <button
                            key={count}
                            onClick={() => setColumnCount(count as 3 | 4 | 5)}
                            className={`px-3 py-1 rounded-md transition-colors text-sm font-medium ${
                              columnCount === count
                                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                                : 'text-slate-300 hover:text-white'
                            }`}
                          >
                            {count}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleExport}
                    disabled={selectedBusinesses.size === 0}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Export CSV
                  </button>

                  {hasGoogleAuth && (
                    <button
                      onClick={handleExportToSheets}
                      disabled={selectedBusinesses.size === 0}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
                        <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                      </svg>
                      Export to Sheets
                    </button>
                  )}

                  <button
                    onClick={handleEnrich}
                    disabled={selectedBusinesses.size === 0}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 7H7v6h6V7z" />
                      <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                    </svg>
                    Enrich Data
                  </button>

                  <button
                    onClick={handleOptimizeRoute}
                    disabled={selectedBusinesses.size < 2}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Optimize Route
                  </button>
                </div>
              </div>
            </div>

            {/* Results Display */}
            {isLoading ? (
              <LoadingSpinner message="Processing..." />
            ) : viewMode === 'card' ? (
              <div className={getGridClasses()}>
                {currentResults.map((business) => (
                  <BusinessCard
                    key={business.id}
                    business={business}
                    isSelected={selectedBusinesses.has(business.id)}
                    onSelectionChange={toggleBusinessSelection}
                  />
                ))}
              </div>
            ) : (
              <BusinessListView
                businesses={currentResults}
                selectedBusinesses={selectedBusinesses}
                onSelectionChange={toggleBusinessSelection}
              />
            )}
          </>
        )}
      </div>

      <GoogleSheetsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleModalExport}
        businesses={exportBusinesses}
      />
    </Layout>
  );
}
